import React, { useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import CMSRender from "aldoo-ra/CMS/cms-render"
import CMSData from "aldoo-ra/CMS/cms-data"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Localization from "aldoo-ra/Localization"
import Image from "aldoo-ra/Image"
import config from "aldoo-ra/Config"


export default function SigninDialog({ controller }) {
  const emailRef = useRef()
  const { tjsx } = Localization()
  const { closeDialog, signin, DialogType, isDialogOpen } = controller

  // State to track if the email input is filled
  const [isEmailEntered, setIsEmailEntered] = useState(false)

  // Check if CMS data is available
  const { data: cmsData, loading: cmsLoading } = CMSData({
    contentType: "SigninDialog",
  })

  const hasCMSData = !cmsLoading && cmsData && cmsData.length > 0

  // Fallback email provider for when CMS data is unavailable
  const fallbackEmailProvider = {
    id: "email",
    name: "Email",
    logo: "/static/images/auth-providers/google.svg", // Using generic icon for email
    placeholder: "Enter your email address",
    cta: "Continue with Email"
  }

  // Fallback dialog content
  const renderFallbackDialog = () => (
    <div className="relative w-full flex flex-col items-center justify-center bg-theme-white md:px-[56px] pb-[12px] mx-auto">
      <div className="w-full max-w-[374px]">
        {/* Logo */}
        <div className="flex items-center justify-center mb-12">
          <Image id="aldoo_pulse_logo_icon" className="h-[53px] w-auto object-contain" />
        </div>

        {/* Divider ("OR") - moved to top for fallback */}
        <div className="text-center mb-[38px]">
          <Typography variant="info_16">
            Sign in with email
          </Typography>
        </div>

        {/* Email Input */}
        <div className="mb-4">
          <div className="flex items-center gap-[10px] px-[20px] py-[10px] bg-gray_100 border border-bordered rounded-full">
            <img
              src={fallbackEmailProvider.logo}
              alt="Email Provider"
              className="w-6 h-6 opacity-50"
            />
            <Typography variant="body1" className="my-0 w-full">
              <input
                type="email"
                ref={emailRef}
                placeholder={fallbackEmailProvider.placeholder}
                onChange={(e) =>
                  setIsEmailEntered(!!e.target.value.trim())
                }
                className="w-full text-text placeholder-gray_700 bg-transparent outline-none"
              />
            </Typography>
          </div>
        </div>

        {/* Sign In Button */}
        <Button
          onClick={() => {
            const email = emailRef.current?.value.trim().toLowerCase()
            if (email) signin(fallbackEmailProvider, email)
          }}
          variant={
            isEmailEntered
              ? "sign_in_option"
              : "sign_in_option_disabled"
          }
          className={isEmailEntered ? "bg-primary hover:bg-primary_dk" : "bg-neutral_2"}
          disabled={!isEmailEntered}
        >
          {fallbackEmailProvider.cta}
        </Button>

        {/* Footer */}
        <div className="mt-[40px] text-center text-sm text-gray_1">
          <Typography variant="txt1_dim">
            By continuing, you agree to our{" "}
            <a
              href="/terms-of-use"
              target="_blank"
              className="text-primary hover:underline"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              className="text-primary hover:underline"
            >
              Privacy Policy
            </a>
          </Typography>
        </div>

      </div>
    </div>
  )

  return (
    <Modal
      isOpen={isDialogOpen(DialogType.SIGNIN)}
      onClose={closeDialog}
      variant="centered"
      className="md:max-w-[534px]"
    >
      {hasCMSData ? (
        <CMSRender
          contentType="SigninDialog"
          renderItem={(dialog) => {
            const emailProvider = dialog.providers.find((p) => p.id === "email")

            return (
              <div className="relative w-full flex flex-col items-center justify-center bg-theme-white md:px-[56px] pb-[12px] mx-auto">
                <div className="w-full max-w-[374px]">
                  {/* Logo */}
                  <div className="flex items-center justify-center mb-12">
                    <Image id="aldoo_pulse_logo_icon" className="h-[53px] w-auto object-contain" />
                  </div>
                  {/* Providers */}
                  <div className="space-y-3">
                    {dialog.providers
                      .filter((item) => item.id !== "email" && item.id !== "apple")
                      .map((provider) => {
                        let className
                        switch (provider.id) {
                          case "apple":
                            className = "bg-[#000000] hover:bg-[#1a1a1a]"
                            break
                          case "google":
                            className = "bg-[#4285F4] hover:bg-[#3367d6]"
                            break
                          default:
                            className = "bg-primary hover:bg-primary_dk"
                        }

                        return (
                          <Button
                            key={provider.id}
                            onClick={() => {
                              const mockCreds = config.MOCK_SIGN_IN_PROVIDERS?.[provider.id.toUpperCase()]
                              signin({ ...provider, ...(mockCreds && { mockCredentials: mockCreds }) })
                            }}
                            variant="sign_in_option"
                            className={` ${className}`}
                          >
                            <img
                              src={provider.logo}
                              alt={provider.name}
                              className="w-6 h-6"
                            />
                            <span>{provider.cta}</span>
                          </Button>
                        )
                      })}
                  </div>

                  {/* Divider ("OR") */}
                  <div className="text-center mt-[15px] mb-[38px]">
                    <Typography variant="info_16">
                      {dialog.divider_text}
                    </Typography>
                  </div>

                  {/* Email Input */}
                  <div className="mb-4">
                    <div className="flex items-center gap-[10px] px-[20px] py-[10px] bg-gray_100 border border-bordered rounded-full">
                      <img
                        src={emailProvider?.logo}
                        alt="Email Provider"
                        className="w-6 h-6 opacity-50"
                      />
                      <Typography variant="body1" className="my-0 w-full">
                        <input
                          type="email"
                          ref={emailRef}
                          placeholder={emailProvider?.placeholder}
                          onChange={(e) =>
                            setIsEmailEntered(!!e.target.value.trim())
                          }
                          className="w-full text-text placeholder-gray_700 bg-transparent outline-none"
                        />
                      </Typography>
                    </div>
                  </div>

                  {/* Sign In Button */}
                  <Button
                    onClick={() => {
                      const email = emailRef.current?.value.trim().toLowerCase()
                      if (email) signin(emailProvider, email)
                    }}
                    variant={
                      isEmailEntered
                        ? "sign_in_option"
                        : "sign_in_option_disabled"
                    }
                    className={isEmailEntered ? "bg-primary hover:bg-primary_dk" : "bg-neutral_2"}
                    disabled={!isEmailEntered}
                  >
                    {emailProvider?.cta}
                  </Button>

                  {/* Footer */}
                  <div className="mt-[40px] text-center text-sm text-gray_1">
                    <Typography variant="txt1_dim">
                      {tjsx(dialog.footer_text, {
                        tos: (
                          <a
                            key={dialog.footer_links[0]._id}
                            href={dialog.footer_links[0].link[0].url}
                            target="_blank"
                            className="text-primary hover:underline"
                          >
                            {dialog.footer_links[0].label}
                          </a>
                        ),
                        pp: (
                          <a
                            key={dialog.footer_links[1]._id}
                            href={dialog.footer_links[1].link[0].url}
                            target="_blank"
                            className="text-primary hover:underline"
                          >
                            {dialog.footer_links[1].label}
                          </a>
                        ),
                      })}
                    </Typography>
                  </div>
                </div>
              </div>
            )
          }}
        />
      ) : (
        renderFallbackDialog()
      )}
    </Modal>
  )
}
